import React from 'react';
import cn from '@/utils/cn';

interface PaginationControlsProps {
  onPageChange: (page: number) => void;
  page: number;
  perPage: number;
  totalCount: number;
  totalPages: number;
}

const NAV_BUTTON_CLASS = cn(
  'tw-px-3 tw-py-1.5 tw-text-sm tw-rounded tw-border tw-border-solid tw-transition-colors',
  'tw-bg-white tw-border-gray-300 tw-text-gray-700',
  'hover:tw-bg-gray-50 hover:tw-text-gray-900',
  'disabled:tw-opacity-40 disabled:tw-cursor-not-allowed disabled:hover:tw-bg-white',
);

const ACTIVE_PAGE_CLASS = cn(
  'tw-px-3 tw-py-1.5 tw-text-sm tw-rounded tw-border tw-border-solid',
  'tw-bg-tci-purple tw-border-tci-purple tw-text-white',
);

const INACTIVE_PAGE_CLASS = cn(
  'tw-px-3 tw-py-1.5 tw-text-sm tw-rounded tw-border tw-border-solid tw-cursor-pointer tw-transition-colors',
  'tw-bg-white tw-border-gray-300 tw-text-gray-700',
  'hover:tw-bg-gray-50 hover:tw-text-gray-900',
);

const buildPages = (current: number, total: number): (number | 'ellipsis')[] => {
  const pages: (number | 'ellipsis')[] = [];
  for (let i = 1; i <= total; i += 1) {
    if (i === 1 || i === total || Math.abs(i - current) <= 1) {
      pages.push(i);
    } else if (pages[pages.length - 1] !== 'ellipsis') {
      pages.push('ellipsis');
    }
  }
  return pages;
};

const PaginationControls = ({
  onPageChange,
  page,
  perPage,
  totalCount,
  totalPages,
}: PaginationControlsProps) => {
  if (totalPages <= 1) {
    if (totalCount === 0) return null;
    return (
      <div className="tw-text-xs tw-text-gray-500 tw-mt-3">
        Showing {totalCount} of {totalCount}
      </div>
    );
  }

  const firstOnPage = (page - 1) * perPage + 1;
  const lastOnPage = Math.min(page * perPage, totalCount);
  const pages = buildPages(page, totalPages);

  return (
    <div className="tw-flex tw-items-center tw-justify-between tw-mt-3 tw-flex-wrap tw-gap-2">
      <div className="tw-text-xs tw-text-gray-500">
        Showing {firstOnPage}–{lastOnPage} of {totalCount.toLocaleString()}
      </div>
      <div className="tw-flex tw-items-center tw-gap-1">
        <button
          className={NAV_BUTTON_CLASS}
          disabled={page <= 1}
          type="button"
          onClick={() => onPageChange(page - 1)}
        >
          Previous
        </button>
        {pages.map((p, idx) => {
          if (p === 'ellipsis') {
            return (
              <span
                key={`ellipsis-${idx}`}
                className="tw-px-2 tw-text-gray-400"
              >
                &hellip;
              </span>
            );
          }
          return (
            <button
              key={p}
              aria-current={p === page ? 'page' : undefined}
              className={p === page ? ACTIVE_PAGE_CLASS : INACTIVE_PAGE_CLASS}
              type="button"
              onClick={() => onPageChange(p)}
            >
              {p}
            </button>
          );
        })}
        <button
          className={NAV_BUTTON_CLASS}
          disabled={page >= totalPages}
          type="button"
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default PaginationControls;
